import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const PLATFORMS = [
  { id: 'youtube', label: 'YouTube', icon: 'Youtube', color: '#FF0000', desc: 'Upload as video' },
  { id: 'tiktok', label: 'TikTok', icon: 'Music2', color: '#000000', desc: 'Share as short' },
  { id: 'instagram', label: 'Instagram', icon: 'Instagram', color: '#E1306C', desc: 'Post as reel' },
  { id: 'link', label: 'Share Link', icon: 'Link', color: 'var(--color-primary)', desc: 'Copy shareable URL' },
];

export default function OneClickPublish({ game, onClose }) {
  const [publishing, setPublishing] = useState(null);
  const [published, setPublished] = useState([]);
  const [copied, setCopied] = useState(false);

  if (!game) return null;

  const handlePublish = async (platform) => {
    if (platform?.id === 'link') {
      const link = `${window.location?.origin}/game-player-screen?id=${game?.id}`;
      try { await navigator.clipboard?.writeText(link); } catch {}
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }
    setPublishing(platform?.id);
    await new Promise(r => setTimeout(r, 1500));
    setPublished(prev => [...prev, platform?.id]);
    setPublishing(null);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => e?.target === e?.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-sm rounded-2xl overflow-hidden animate-bounce-in"
        style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-xl)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.12)' }}>
              <Icon name="Send" size={16} color="var(--color-success)" />
            </div>
            <div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>One-Click Publish</h3>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{game?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            Publish your creation directly to your favorite platforms with one click.
          </p>

          {PLATFORMS?.map((platform) => {
            const isPublishing = publishing === platform?.id;
            const isPublished = published?.includes(platform?.id);
            const isCopied = platform?.id === 'link' && copied;

            return (
              <button
                key={platform?.id}
                onClick={() => handlePublish(platform)}
                disabled={isPublishing || isPublished}
                className="flex items-center gap-3 p-3 rounded-xl border transition-all hover:opacity-90 disabled:opacity-70"
                style={{
                  borderColor: isPublished || isCopied ? 'var(--color-success)' : `${platform?.color}40`,
                  background: isPublished || isCopied ? 'rgba(16,185,129,0.08)' : `${platform?.color}08`,
                }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: isPublished || isCopied ? 'rgba(16,185,129,0.15)' : `${platform?.color}18` }}
                >
                  <Icon
                    name={isPublished || isCopied ? 'CheckCircle2' : isPublishing ? 'Loader' : platform?.icon}
                    size={18}
                    color={isPublished || isCopied ? 'var(--color-success)' : platform?.color}
                  />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold" style={{ color: isPublished || isCopied ? 'var(--color-success)' : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
                    {isPublished ? `Published to ${platform?.label}!` : isCopied ? 'Link Copied!' : platform?.label}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                    {isPublishing ? 'Publishing...' : platform?.desc}
                  </p>
                </div>
                {!isPublished && !isCopied && !isPublishing && (
                  <Icon name="ArrowRight" size={14} color="var(--color-muted-foreground)" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
